import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, useTheme, Paper } from "@material-ui/core";
import StandardTabs from "../../components/StandardTabs";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import ResellerPlansManager from "../../components/ResellerPlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import EmailSettings from "../../components/Settings/EmailSettings";
import BrandKitIA from "../../components/Settings/BrandKitIA"; // [ANTIGRAVITY]
import AiPackages from "../AiPackages";
import CronManager from "../../components/CronManager";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";

const SETTINGS_TAB_COLORS = {
  options: "#00E5FF",
  brandKitIA: "#E040FB",
  email: "#FF9100",
  schedules: "#00E676",
  finalizacao: "#FFD600",
  companies: "#00E5FF",
  plans: "#2979FF",
  resellerPlans: "#7C4DFF",
  helps: "#00B0FF",
  whitelabel: "#FF4081",
  aiPackages: "#00E676",
  crons: "#76FF03",
};

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: theme.spacing(2),
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    marginTop: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  tab: {
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0F172A",
    borderRadius: 8,
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #E2E8F0",
    boxShadow: theme.palette.type === "dark" ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)",
    marginBottom: theme.spacing(1),
    "& .MuiTab-root": {
      color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.65)" : "#64748B",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.85rem",
      minHeight: 46,
      transition: "all 0.2s ease",
      "&:hover": {
        color: theme.palette.type === "dark" ? "#ffffff" : "#0F172A",
      },
    },
    "& .Mui-selected": {
      color: theme.palette.type === "dark" ? "#ffffff !important" : "#0F172A !important",
      fontWeight: "bold",
    },
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    width: "100%",
    backgroundColor: "transparent",
  },
  paperCompanies: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    width: "100%",
    backgroundColor: "transparent",
  },
  headerDark: {
    backgroundColor: "#1e1e1e",
    color: "#ffffff",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "4px 4px 0 0",
    marginBottom: "10px",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();

  const getTabColor = (currentTab) => {
    return theme.palette.primary.main;
  };

  const [tab, setTab] = useState(() => { try { const p = new URLSearchParams(window.location.search).get("tab"); return p || "options"; } catch (e) { return "options"; } });
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      setTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
  }, []);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success(i18n.t("settings.success.hours"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return (user && user.super) || (currentUser && currentUser.super);
  };

  return (
    <MainContainer className={classes.root}>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t("settings.title")}</Title>
          </MainHeader>
          <Paper className={classes.mainPaper} elevation={1}>
            {(() => {
              const settingsTabs = [
                { value: "options", label: i18n.t("settings.tabs.options") },
                { value: "brandKitIA", label: i18n.t("settings.tabs.kitMarcaIA") },
                ...((user.profile === "admin" || isSuper())
                  ? [{ value: "email", label: i18n.t("settings.tabs.email") }]
                  : []),
                ...(schedulesEnabled
                  ? [{ value: "schedules", label: i18n.t("settings.tabs.schedules") }]
                  : []),
                ...(user.profile === "admin" && user.finalizacaoComValorVendaAtiva
                  ? [{ value: "finalizacao", label: i18n.t("settings.tabs.finalization") || "Cierre de Atención" }]
                  : []),
                ...(isSuper()
                  ? [
                      { value: "companies", label: i18n.t("settings.tabs.companies") },
                      { value: "plans", label: i18n.t("settings.tabs.plans") },
                      { value: "resellerPlans", label: i18n.t("settings.tabs.resellerPlans") },
                      { value: "helps", label: i18n.t("settings.tabs.helps") },
                    ]
                  : []),
                { value: "whitelabel", label: "Whitelabel" },
                ...(isSuper()
                  ? [
                      { value: "aiPackages", label: i18n.t("aiPackages.title") },
                      { value: "crons", label: i18n.t("settings.tabs.crons") },
                    ]
                  : []),
              ];
              return (
                <div style={{ padding: "12px 16px 8px 16px" }}>
                  <StandardTabs
                    value={tab}
                    onChange={handleTabChange}
                    items={settingsTabs}
                    maxVisible={4}
                  />
                </div>
              );
            })()}
            <div id='settings-scroll-area' className={classes.scrollArea}>
            <Paper className={tab === "companies" ? classes.paperCompanies : classes.paper} elevation={0}>
              {/* Renderização condicional simples - apenas a aba ativa renderiza */}
              {tab === "email" && (user.profile === "admin" || isSuper()) && (
                <div className={classes.container}>
                  <EmailSettings
                    settings={settings}
                    onSettingsChange={(newSettings) => setSettings(newSettings)}
                  />
                </div>
              )}

              {tab === "schedules" && (
                <div className={classes.container}>
                  <SchedulesForm
                    loading={loading}
                    onSubmit={handleSubmitSchedules}
                    initialValues={schedules}
                  />
                </div>
              )}

              {tab === "companies" && isSuper() && (
                <div className={classes.container}>
                  <CompaniesManager />
                </div>
              )}

              {tab === "plans" && isSuper() && (
                <div className={classes.container}>
                  <PlansManager />
                </div>
              )}

              {tab === "resellerPlans" && isSuper() && (
                <div className={classes.container}>
                  <ResellerPlansManager />
                </div>
              )}

              {tab === "helps" && isSuper() && (
                <div className={classes.container}>
                  <HelpsManager />
                </div>
              )}

              {tab === "whitelabel" && (
                <div className={classes.container}>
                  <Whitelabel settings={oldSettings} />
                </div>
              )}
              
              {tab === "aiPackages" && isSuper() && (
                <div className={classes.container}>
                  <AiPackages />
                </div>
              )}
              
              {tab === "crons" && isSuper() && (
                <div className={classes.container}>
                  <CronManager />
                </div>
              )}

              {tab === "finalizacao" && (
                <div className={classes.container}>
                  <FinalizacaoAtendimento
                    settings={settings}
                    onSettingsChange={(newSettings) => setSettings(newSettings)}
                  />
                </div>
              )}

              {tab === "options" && (
                <div className={classes.container}>
                  <Options
                    settings={settings}
                    oldSettings={oldSettings}
                    user={currentUser}
                    scheduleTypeChanged={(value) =>
                      setSchedulesEnabled(value === "company")
                    }
                  />
                </div>
              )}

              {tab === "brandKitIA" && (
                <div className={classes.container}>
                  <BrandKitIA 
                    settings={settings}
                    onSettingsChange={(newSettings) => setSettings(newSettings)}
                  />
                </div>
              )}
            </Paper>
            </div>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;
